// Libraries
import { HeaderLayout } from '../../layouts';
import { Card, Alert, FormElems, LoginFlowComps } from '../../components';
import {
    resetPasswordRequest,
    resetPasswordError,
    markUserActiveRequest,
} from '../../redux/common/auth/auth.actions';
import resetImg from '../../static/images/resetimg.png';
import greenTick from '../../static/images/green-tick-icon.png';
import validate from './validator';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Field, reduxForm } from 'redux-form';

// Layouts

// Components

// Actions

// Images

// Validator

// Styles
import './setPassword.scss';

const passwordRules = [
    {
        label: 'At least 8 characters',
        validator: str => {
            return str.length >= 8;
        },
        isValid: false,
    },
    {
        label: 'At least One uppercase',
        validator: str => {
            return /[A-Z]/.test(str);
        },
        isValid: false,
    },
    {
        label: 'At least One lowercase',
        validator: str => {
            return /[a-z]/.test(str);
        },
        isValid: false,
    },
    {
        label: 'At least one number',
        validator: str => {
            return /[0-9]/.test(str);
        },
        isValid: false,
    },
    {
        label: 'At least one special characters',
        validator: str => {
            return /[\W_]/.test(str);
        },
        isValid: false,
    },
];

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

const SetPassword = props => {
    const { handleSubmit, actions, auth } = props;

    const [passwordRulesOpts, setPasswordRulesOpts] = useState(passwordRules);
    const [showStrengthTooltip, setShowStrengthTooltip] = useState(false);

    const { linkToken } = useParams();

    const pageType = useQueryParams().get('type');

    // Reset resetPasswordError error
    const resetError = useCallback(() => {
        if (auth.resetPasswordError) {
            actions.resetPasswordError({
                resetPasswordError: '',
            });
        }
    }, [actions, auth.resetPasswordError]);

    useEffect(() => {
        return () => {
            // Reset resetPasswordError error
            resetError();
        };
    }, [resetError]);

    useEffect(() => {
        actions.markUserActiveRequest({ linkToken });
    }, [actions, linkToken]);

    /**
     * Called on login form submit
     *
     * @param {Object} values - Object of all form field values
     * @returns {undefined}
     */
    function handleResetSubmit(values) {
        actions.resetPasswordRequest({
            linkToken,
            pageType,
            ...values,
        });
    }

    function onPassFieldBlur() {
        setShowStrengthTooltip(false);
    }

    function onPassFieldFocus() {
        setShowStrengthTooltip(true);
    }

    function onPassFieldChange(val) {
        const updatedRulesOpts = passwordRulesOpts.map(rule => ({
            ...rule,
            isValid: rule.validator(val),
        }));
        setPasswordRulesOpts(updatedRulesOpts);
    }

    return (
        <HeaderLayout>
            <div className="set-pass-wrapper">
                <Card customClass="set-pass-card">
                    <LoginFlowComps.ImageSplitView splitImg={resetImg}>
                        <div className="set-pass-form-wrapper">
                            <div className="form-head">
                                {pageType === 'reset' ? 'Reset' : 'Set'}{' '}
                                Password
                            </div>
                            <div className="form-subhead">
                                {pageType === 'reset'
                                    ? 'Reset your login password'
                                    : 'Create new password for login'}
                            </div>
                            <form
                                className="set-pass-form"
                                onSubmit={handleSubmit(handleResetSubmit)}
                                onChange={resetError}
                            >
                                <div className="form-field new-password-field">
                                    <p className="label">New Password</p>
                                    <Field
                                        name="newPassword"
                                        type="password"
                                        component={FormElems.Input}
                                        placeholder="New Password"
                                        wrapperClass="password-field-wrapper"
                                        handleBlur={onPassFieldBlur}
                                        handleFocus={onPassFieldFocus}
                                        onChange={onPassFieldChange}
                                    />
                                    <div className="strength-viewer">
                                        {passwordRulesOpts.map(rule => (
                                            <div
                                                key={rule.label}
                                                className="strength-bar"
                                                style={{
                                                    background: rule.isValid
                                                        ? '#92b36b'
                                                        : '#d8d8d8',
                                                }}
                                            />
                                        ))}
                                    </div>
                                    {showStrengthTooltip && (
                                        <div className="strength-tooltip">
                                            Password Strength
                                            <ul className="rules-list">
                                                {passwordRulesOpts.map(rule => (
                                                    <li
                                                        key={rule.label}
                                                        style={{
                                                            color: rule.isValid
                                                                ? '#92b36b'
                                                                : '#6d6e71',
                                                        }}
                                                    >
                                                        <div className="tick-wrapper">
                                                            {rule.isValid && (
                                                                <img
                                                                    src={
                                                                        greenTick
                                                                    }
                                                                    alt=""
                                                                />
                                                            )}
                                                        </div>
                                                        {rule.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                                <div className="form-field">
                                    <p className="label">Confirm Password</p>
                                    <Field
                                        name="confirmPassword"
                                        type="password"
                                        component={FormElems.Input}
                                        placeholder="Confirm Password"
                                    />
                                </div>

                                {auth.resetPasswordError && (
                                    <Alert>{auth.resetPasswordError}</Alert>
                                )}

                                <div className="form-btn">
                                    <FormElems.Button
                                        buttonType="primary"
                                        text="Submit"
                                    />
                                    <Link to="/login">Back to Login</Link>
                                </div>
                            </form>
                        </div>
                    </LoginFlowComps.ImageSplitView>
                </Card>
            </div>
        </HeaderLayout>
    );
};

SetPassword.propTypes = {
    actions: PropTypes.object,
    auth: PropTypes.object,
    handleSubmit: PropTypes.func,
};

const mapStateToProps = ({ auth }) => ({
    auth,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            resetPasswordRequest,
            resetPasswordError,
            markUserActiveRequest,
        },
        dispatch
    ),
});

export default compose(
    reduxForm({
        form: 'set-password',
        validate,
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(SetPassword);
